import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Typography, Grid, Stack, IconButton } from '@mui/material';
import {
  Add as AddIcon,
  WarningAmber as AttentionIcon,
  ChangeCircleOutlined as ReAssignIcon,
} from '@mui/icons-material';
import moment from 'moment';

import {
  FBoxContainer,
  FCheckbox,
  FHAntdDateTimePicker,
  FHButton,
  FHChip,
  FHPageLink,
  FHSelect,
  FHSideDrawer,
  FHSkeletonLoader,
  FHTooltip,
  FSearch,
} from '../../../components/base';
import {
  clientActions,
  cohortActions,
  facilityActions,
  payorActions,
  providerActions,
  providerAssignmentActions,
  treatmentPlanActions,
} from '../../../store';
import {
  FORTA_BLUE,
  FORTA_ERROR,
  FORTA_SUCCESS,
  FORTA_WHITE,
  FORTA_ORANGE,
} from '../../../utils/colors';
import { handleSearch, searchFieldExtractors } from '../../utils/search';
import { areUserRolesIn, capitalizeFirstLetter, history } from '../../../helpers';
import useSortableData from '../../hooks/useSortableData';
import { useCanManageModifiers } from '../../hooks';
import ClientModifierMatrixSection from '../../components/clientModifierMatrixSection';
import PageContainer from '../../../components/layout/PageContainer';
import ProviderAssignmentRow from '../../components/ProviderAssignmentRow';
import EditOutlinedIcon from '../../../components/svgs/EditOutlinedIcon';
import CohortAssignmentRow from '../../components/CohortAssignmentRow';
import FacilityAssignmentRow from '../../components/FacilityAssignmentRow';
import AdminRowLoader from '../../components/AdminRowLoader';
import FHKeyValue from '../../../components/base/FHKeyValue';
import TableHeaderGenerator from '../../components/TableHeaderGenerator';
import { getProviderAssignmentStatus, getUtcOffset } from '../../../utils/common';
import {
  NEW_PROVIDER_ASSIGNMENT_ID,
  LABELS,
  NEW_ASSIGNMENT_WITHOUT_CLIENT,
  NEW_COHORT_ASSIGNMENT_WITHOUT_CLIENT,
  NEW_FACILITY_ASSIGNMENT_WITHOUT_CLIENT,
} from './constants/general';
import DeleteCohortAssignmentModal from './components/deleteCohortAssignmentModal';
import {
  cohortAssignmentColumns,
  facilityAssignmentColumns,
  providerAssignmentColumns,
  policyColumns,
  treatmentPlanColumns,
} from '../../configs/tableHeaders.config';
import {
  AssignmentStatus,
  ClinicalRelationships,
  CohortAssignmentMode,
  FacilityAssignmentType,
  Roles,
} from '../../../constants';
import {
  ClinicalStatus,
  ClientClinicalStatusReason,
  ProviderClinicalStatus,
} from '../../constants/general';

import type { ITableHeader } from '../../components/TableHeaderGenerator';
import type { RootState } from '../../../store';
import type { Moment } from 'moment';
import type { ChangeEvent } from 'react';
import type {
  IClientFacilityMap,
  IClientAssignment,
  IClientPolicyMap,
  IProvider,
  IProviderAssignment,
  ITreatmentPlan,
  SelectValueType,
  IClient,
} from '../../../utils/types';

const isClinicalStatus = (value: SelectValueType): value is ClinicalStatus =>
  Object.values(ClinicalStatus).some((status) => status === value);

const isClinicalStatusReason = (
  value: SelectValueType,
): value is ClientClinicalStatusReason =>
  Object.values(ClientClinicalStatusReason).some((reason) => reason === value);

function ClientDetails() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { user } = useSelector((state: RootState) => state.auth);
  const {
    selected: client,
    loading: clientLoading,
    selectedClientPlans,
    selectedClientFacility,
    list: clinicalClientList,
    selectedClientPolicyLoading,
    allClientsList,
    facilityAssignmentsLoading,
  } = useSelector((state: RootState) => state.clients);

  const { loading: facilityLoading } = useSelector((state: RootState) => state.facilities);
  const { loading: payorsLoading } = useSelector((state: RootState) => state.payors);
  const { allProvidersList = [], loading: providerLoading } = useSelector(
    (state: RootState) => state.providers,
  );
  const { list: treatmentPlans, loading: treatmentPlanLoading } = useSelector(
    (state: RootState) => state.treatmentPlans,
  );
  const { list: providerAssignments = [], loading: providerAssignmentLoading } = useSelector(
    (state: RootState) => state.providerAssignment,
  );
  const { clientCohortAssignments, loading: cohortAssignmentLoading } = useSelector(
    (state: RootState) => state.cohorts,
  );

  const { clientId } = useParams();
  const getTPEditingStatusfromId = (id: number | undefined): string => {
    if (id === undefined) {
      return '';
    }
    if (id === 1 || id === 2 || id === 3 || id === 4) {
      return LABELS[id];
    }
    return '';
  };

  const [selectedClinicalStatus, setSelectedClinicalStatus] = useState<string>('');
  const [selectedClinicalStatusReason, setSelectedClinicalStatusReason] = useState<string>('');
  const [clientFacilities, setClientFacilities] = useState<Array<IClientFacilityMap>>([]);
  const [billingFacilities, setBillingFacilites] = useState<Array<IClientFacilityMap>>([]);
  const [serviceFacilities, setServiceFacilites] = useState<Array<IClientFacilityMap>>([]);
  const [isFacilityDataFiltered, setIsFacilityDataFiltered] = useState<boolean>(false);
  const [assignments, setAssignments] = useState<IProviderAssignment[]>([]);
  const [clientPolicies, setClientPolicies] = useState<IClientPolicyMap[]>([]);
  const [editableAssignmentId, setEditableAssignmentId] = useState<number | null>(null);
  const [editableCohortIndex, setEditableCohortIndex] = useState<number | null>(null);
  const [editableFacilityIndex, setEditableFacilityIndex] = useState<number | null>(null);
  const [isNewAssignmentAdding, setIsNewAssignmentAdding] = useState<boolean>(false);
  const [isNewCohortAdding, setIsNewCohortAdding] = useState<boolean>(false);
  const [cohortAssignments, setCohortAssignments] = useState<IClientAssignment[]>([]);
  const [clientTreatmentPlans, setClientTreatmentPlans] = useState<ITreatmentPlan[]>([]);
  const [isNewFacilityAdding, setIsNewFacilityAdding] = useState<boolean>(false);
  const [treatmentPlanSearchStr, setTreatmentPlanSearchStr] = useState<string>('');
  const [cohortSearchStr, setCohortSearchStr] = useState<string>('');
  const [providerSearchStr, setProviderSearchStr] = useState<string>('');
  const [facilitySearchStr, setFacilitySearchStr] = useState<string>('');
  const [policySearchStr, setPolicySearchStr] = useState<string>('');
  const [currentHomeBCBA, setCurrentHomeBCBA] = useState<IProvider | null>(null);
  const [pendingHomeBCBA, setPendingHomeBCBA] = useState<IProvider | null>(null);
  const [showAllAssignments, setShowAllAssignments] = useState<boolean>(false);
  const [assementAllocationDate, setAssessmentAllocationDate] = useState<string | null>(
    moment().format('YYYY-MM-DD'),
  );

  const [showPolicyPreview, setShowPolicyPreview] = useState<boolean>(false);
  const [selectedPolicyForPreview, setSelectedPolicyForPreview] = useState<IClientPolicyMap>();
  const [cohortDeleteModalOpen, setCohortDeleteModalOpen] = useState(false);
  const [cohortAssignmentPendingDelete, setCohortAssignmentPendingDelete] =
    useState<IClientAssignment | null>(null);

  const clinicalClient = client?.clinical_status === ClinicalStatus.CLINICAL;
  const showClinicalStatusSection = areUserRolesIn(user?.roles, [
    Roles.Admin,
    Roles.OnboardingCoordinator,
  ]);
  // Admin + Biller may manage the client's billing modifier matrix.
  const canManageModifiers = useCanManageModifiers();

  const {
    data: sortedProviderAssignments,
    handleSorting: sortProviderAssignments,
    sortField: providerAssignmentsSortField,
    sortOrder: providerAssignmentsSortOrder,
  } = useSortableData(assignments, 'date_start', 'desc');

  const {
    data: sortedFacilityAssignments,
    handleSorting: sortFacilityAssignments,
    sortField: facilityAssignmentsSortField,
    sortOrder: facilityAssignmentsSortOrder,
  } = useSortableData(clientFacilities, 'date_start', 'desc');

  const {
    data: sortedCohortAssignments,
    handleSorting: sortCohortAssignments,
    sortField: cohortAssignmentsSortField,
    sortOrder: cohortAssignmentsSortOrder,
  } = useSortableData(cohortAssignments, 'date_start', 'desc');

  const {
    data: sortedTreatmentPlans,
    handleSorting: sortTreatmentPlans,
    sortField: treatmentPlansSortField,
    sortOrder: treatmentPlansSortOrder,
  } = useSortableData(clientTreatmentPlans, 'date_treatment_plan_started', 'desc');

  const {
    data: sortedPolicies,
    handleSorting: sortPolicies,
    sortField: policySortField,
    sortOrder: policySortOrder,
  } = useSortableData(clientPolicies, 'date_end', 'desc');

  const filteredProviderAssignment = useMemo(() => {
    if (showAllAssignments) return sortedProviderAssignments;

    return sortedProviderAssignments.filter((assignment) => {
      const isActive =
        getProviderAssignmentStatus(assignment) === AssignmentStatus.ASSIGNED ||
        getProviderAssignmentStatus(assignment) === AssignmentStatus.PENDING;
      if (!isActive && assignment.id !== undefined) {
        return false;
      }
      return true;
    });
  }, [sortedProviderAssignments, showAllAssignments]);

  useEffect(() => {
    dispatch(facilityActions.getList({ getAccessTokenSilently }));
    dispatch(payorActions.getList({ getAccessTokenSilently }));

    return () => {
      dispatch(clientActions.clearSelectedClient());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!clientId) return;
    setAssignments(providerAssignments);
    setClientPolicies(selectedClientPlans);
    setClientTreatmentPlans(treatmentPlans);
  }, [clientId, providerAssignments, selectedClientPlans, treatmentPlans]);

  useEffect(() => {
    if (!providerAssignments || !treatmentPlans) return;
    setCurrentHomeBCBA(
      providerAssignments.find(
        (assignment) =>
          assignment.clinical_relationship === ClinicalRelationships.HomeBcba &&
          getProviderAssignmentStatus(assignment) === AssignmentStatus.ASSIGNED,
      )?.provider || null,
    );
    setPendingHomeBCBA(
      providerAssignments.find(
        (assignment) =>
          assignment.clinical_relationship === ClinicalRelationships.HomeBcba &&
          getProviderAssignmentStatus(assignment) === AssignmentStatus.PENDING,
      )?.provider || null,
    );
  }, [providerAssignments, treatmentPlans]);

  useEffect(() => {
    if (!clientId) return;

    dispatch(clientActions.getClient({ getAccessTokenSilently, clientId }));
    dispatch(clientActions.getClientSpecificFacilityMapping({ getAccessTokenSilently, clientId }));
    dispatch(clientActions.getClientSpecificPayorsMapping({ getAccessTokenSilently, clientId }));
    dispatch(
      treatmentPlanActions.getTreatmentPlanByQuery({
        query: { clientId },
        getAccessTokenSilently,
      }),
    );
    dispatch(
      cohortActions.getClientCohortAssignments({
        params: { client_id: clientId },
        getAccessTokenSilently,
      }),
    );

    dispatch(
      providerAssignmentActions.getList({
        getAccessTokenSilently,
        active_only: false,
        client_id: clientId,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  useEffect(() => {
    if (!clientFacilities || clientFacilities?.length == 0) return;
    setBillingFacilites(
      clientFacilities.filter(
        (cf) =>
          (!cf.date_end || moment(cf.date_end).isSameOrAfter(moment(), 'day')) &&
          cf.assignment_type === FacilityAssignmentType.BILLING,
      ),
    );
    setServiceFacilites(
      clientFacilities.filter(
        (cf) =>
          (!cf.date_end || moment(cf.date_end).isSameOrAfter(moment(), 'day')) &&
          cf.assignment_type === FacilityAssignmentType.SERVICE,
      ),
    );
    setIsFacilityDataFiltered(true);
  }, [clientFacilities]);

  useEffect(() => {
    if (!selectedClientFacility) return;
    setClientFacilities(selectedClientFacility);
  }, [selectedClientFacility]);

  useEffect(() => {
    if (!clientCohortAssignments) return;
    setCohortAssignments(clientCohortAssignments);
  }, [clientCohortAssignments]);

  const policyErrors = useMemo(() => {
    return selectedClientPlans.reduce<JSX.Element[]>((errors, policy: IClientPolicyMap) => {
      void policy;
      return errors.map((error, index) => <Box key={index}>{error}</Box>);
    }, []);
  }, [selectedClientPlans]);

  const modifierPayorOptions = useMemo(
    () =>
      selectedClientPlans
        .filter(
          (policy, index, arr) => arr.findIndex((p) => p.payor.id === policy.payor.id) === index,
        )
        .map((policy) => ({
          value: policy.payor.id,
          label: policy.payor.label,
        })),
    [selectedClientPlans],
  );

  const handleAddProviderAssignment = () => {
    if (!client) {
      return;
    }
    if (!allProvidersList || allProvidersList.length === 0) {
      dispatch(
        providerActions.getList({
          getAccessTokenSilently,
          include_roles: false,
          clinical_status: [
            ProviderClinicalStatus.CLINICAL,
            ProviderClinicalStatus.PRE_CLINICAL,
            ProviderClinicalStatus.OFFBOARDED,
            ProviderClinicalStatus.ON_HOLD,
            ProviderClinicalStatus.LIMITED_ACCESS_GUARDIAN,
            ProviderClinicalStatus.GUARDIAN_INVITED,
          ],
        }),
      );
    }

    setAssignments([{ ...NEW_ASSIGNMENT_WITHOUT_CLIENT, client }, ...assignments]);
    setEditableAssignmentId(NEW_PROVIDER_ASSIGNMENT_ID);
    setIsNewAssignmentAdding(true);
  };

  const canAddCohortAssignment = areUserRolesIn(user?.roles, [
    Roles.Admin,
    Roles.OnboardingCoordinator,
  ]);

  const canModifyCohortAssignment = areUserRolesIn(user?.roles, [Roles.Admin]);

  const handleAddCohortAssignment = () => {
    if (!client) {
      return;
    }
    setCohortAssignments([
      { ...NEW_COHORT_ASSIGNMENT_WITHOUT_CLIENT, client },
      ...cohortAssignments,
    ]);
    setEditableCohortIndex(0);
    setIsNewCohortAdding(true);
  };

  const handleAddFacilityAssignment = () => {
    if (!client) {
      return;
    }
    setClientFacilities([
      { ...NEW_FACILITY_ASSIGNMENT_WITHOUT_CLIENT, client },
      ...clientFacilities,
    ]);
    setEditableFacilityIndex(0);
    setIsNewFacilityAdding(true);
  };
  const handleCancelAssignment = () => {
    setAssignments(providerAssignments);
    setEditableAssignmentId(null);
    setIsNewAssignmentAdding(false);
  };

  const handleCancelCohortAssignment = () => {
    setCohortAssignments(clientCohortAssignments);
    setEditableCohortIndex(null);
    setIsNewCohortAdding(false);
  };

  const handleCancelFacilityAssignment = () => {
    setClientFacilities(selectedClientFacility);
    setEditableFacilityIndex(null);
    setIsNewFacilityAdding(false);
  };

  const handleTreatmentPlanSearch = (searchStr: string) => {
    handleSearch(
      searchStr,
      treatmentPlans,
      (tp: ITreatmentPlan) => [
        `${tp?.provider?.first_name} ${tp?.provider?.last_name}`,
        getTPEditingStatusfromId(tp.status_id),
        tp.id?.toString(),
      ],
      setTreatmentPlanSearchStr,
      setClientTreatmentPlans,
    );
  };

  const handleProviderSearch = (searchStr: string) => {
    handleSearch(
      searchStr,
      providerAssignments,
      (assignment: IProviderAssignment) => [
        ...searchFieldExtractors.nameFields(assignment?.provider),
        assignment?.clinical_relationship,
      ],
      setProviderSearchStr,
      setAssignments,
    );
  };

  const handleCohortSearch = (searchStr: string) => {
    handleSearch(
      searchStr,
      clientCohortAssignments,
      (assignment: IClientAssignment) => [assignment?.cohort?.name],
      setCohortSearchStr,
      setCohortAssignments,
    );
  };

  const handleFacilitySearch = (searchStr: string) => {
    handleSearch(
      searchStr,
      selectedClientFacility,
      (facility: IClientFacilityMap) => [facility?.facility?.label, facility?.assignment_type],
      setFacilitySearchStr,
      setClientFacilities,
    );
  };

  const handlePolicySearch = (searchStr: string) => {
    handleSearch(
      searchStr,
      selectedClientPlans,
      (policy: IClientPolicyMap) => [
        policy?.payor?.label,
        policy?.group_number,
        policy?.relationship_to_policyholder,
        policy?.payor_responsibility,
      ],
      setPolicySearchStr,
      setClientPolicies,
    );
  };

  const handleAssignProvider = async (assignment: IProviderAssignment) => {
    try {
      const response = assignment.id
        ? await dispatch(
            providerAssignmentActions.updateProviderAssignment({
              providerAssignmentId: assignment.id,
              data: {
                date_start: moment(assignment.date_start).format('YYYY-MM-DD'),
                date_end: assignment.date_end
                  ? moment(assignment.date_end).format('YYYY-MM-DD')
                  : null,
              },
              getAccessTokenSilently,
            }),
          )
        : await dispatch(
            providerAssignmentActions.createProviderAssignment({
              data: {
                provider_id: assignment.provider.id,
                client_id: assignment.client.id,
                clinical_relationship: assignment.clinical_relationship,
                date_start: moment(assignment.date_start).format('YYYY-MM-DD'),
                date_end: assignment.date_end
                  ? moment(assignment.date_end).format('YYYY-MM-DD')
                  : null,
              },
              getAccessTokenSilently,
            }),
          );

      if (!response.error) {
        dispatch(
          providerAssignmentActions.getList({
            getAccessTokenSilently,
            active_only: false,
            client_id: clientId,
          }),
        );
        setEditableAssignmentId(null);
        setIsNewAssignmentAdding(false);
      }
    } catch (error) {
      toast.error(`Error updating/creating contract:${error}`);
    }
  };

  const handleDeleteAssignment = async (assignment: IProviderAssignment) => {
    try {
      const response = await dispatch(
        providerAssignmentActions.deleteProviderAssignment({
          providerAssignmentId: assignment.id,
          getAccessTokenSilently,
        }),
      );

      if (!response.error) {
        dispatch(
          providerAssignmentActions.getList({
            getAccessTokenSilently,
            active_only: false,
            client_id: clientId,
          }),
        );
        setEditableAssignmentId(null);
        setIsNewAssignmentAdding(false);
      }
    } catch (error) {
      toast.error(`Error unassigning provider:${error}`);
    }
  };

  const handleAssignFacility = async (assignment: IClientFacilityMap) => {
    try {
      const response = assignment.id
        ? await dispatch(
            clientActions.updateFacilityAssignment({
              clientFacilityAssignmentId: assignment.id,
              data: {
                date_start: moment(assignment.date_start).format('YYYY-MM-DD'),
                date_end: assignment.date_end
                  ? moment(assignment.date_end).format('YYYY-MM-DD')
                  : null,
              },
              getAccessTokenSilently,
            }),
          )
        : await dispatch(
            clientActions.assignFacility({
              data: {
                facility_id: assignment.facility.id,
                client_id: assignment.client.id,
                assignment_type: assignment.assignment_type,
                date_start: moment(assignment.date_start).format('YYYY-MM-DD'),
                date_end: assignment.date_end
                  ? moment(assignment.date_end).format('YYYY-MM-DD')
                  : null,
              },
              getAccessTokenSilently,
            }),
          );
      if (!response.error) {
        dispatch(
          clientActions.getClientSpecificFacilityMapping({
            getAccessTokenSilently,
            clientId,
          }),
        );
        setEditableFacilityIndex(null);
        setIsNewFacilityAdding(false);
      }
    } catch (error) {
      toast.error(`Error updating/creating contract:${error}`);
    }
  };

  const handleAssignCohort = async (assignment: IClientAssignment) => {
    try {
      const response = assignment.id
        ? await dispatch(
            cohortActions.updateClientCohortAssignment({
              assignmentId: assignment.id,
              data: {
                cohort_id: assignment.cohort?.id,
                client_id: assignment.client.id,
                date_start: moment(assignment.date_start).format('YYYY-MM-DD'),
                date_end: assignment.date_end
                  ? moment(assignment.date_end).format('YYYY-MM-DD')
                  : null,
              },
              getAccessTokenSilently,
            }),
          )
        : await dispatch(
            cohortActions.createClientCohortAssignment({
              data: {
                cohort_id: assignment.cohort?.id,
                client_id: assignment.client.id,
                date_start: moment(assignment.date_start).format('YYYY-MM-DD'),
                date_end: assignment.date_end
                  ? moment(assignment.date_end).format('YYYY-MM-DD')
                  : null,
              },
              getAccessTokenSilently,
            }),
          );
      if (!response.error) {
        dispatch(
          cohortActions.getClientCohortAssignments({
            params: { client_id: clientId },
            getAccessTokenSilently,
          }),
        );
        setEditableCohortIndex(null);
        setIsNewCohortAdding(false);
        setCohortSearchStr('');
      }
    } catch (error) {
      toast.error(`Error updating/creating contract:${error}`);
    }
  };

  const closeDeleteCohortModal = () => {
    setCohortDeleteModalOpen(false);
    setCohortAssignmentPendingDelete(null);
  };

  const handleDeleteCohortAssignment = (assignment: IClientAssignment) => {
    setCohortAssignmentPendingDelete(assignment);
    setCohortDeleteModalOpen(true);
  };

  const handleDeleteFacilityAssignment = async (assignment: IClientFacilityMap) => {
    try {
      const response = await dispatch(
        clientActions.deleteFacilityAssignment({
          clientFacilityAssignmentId: assignment.id,
          getAccessTokenSilently,
        }),
      );
      if (!response.error) {
        dispatch(
          clientActions.getClientSpecificFacilityMapping({
            getAccessTokenSilently,
            clientId,
          }),
        );
        setEditableFacilityIndex(null);
      }
    } catch (error) {
      toast.error(`Error deleting contract:${error}`);
    }
  };

  const handleReassignTP = async (tpId: number) => {
    if (tpId && currentHomeBCBA) {
      const response = await dispatch(
        treatmentPlanActions.reassignTreatmentPlan({
          treatmentPlanId: tpId,
          data: {
            provider_id: currentHomeBCBA.id,
            utc_offset_date_assigned: getUtcOffset(moment(new Date())),
          },
          getAccessTokenSilently,
        }),
      );
      if (!response.error) {
        dispatch(
          treatmentPlanActions.getTreatmentPlanByQuery({
            query: { clientId },
            getAccessTokenSilently,
          }),
        );
      }
    }
  };

  const ProviderAssignmentTableHeader = ({ columns, handleSorting }: ITableHeader) => (
    <TableHeaderGenerator
      columns={columns}
      handleSorting={handleSorting}
      sortField={providerAssignmentsSortField}
      sortOrder={providerAssignmentsSortOrder}
      disableSorting={isNewAssignmentAdding || editableAssignmentId !== null}
    />
  );

  const FacilityAssignmentTableHeader = ({ columns, handleSorting }: ITableHeader) => (
    <TableHeaderGenerator
      columns={columns}
      handleSorting={handleSorting}
      sortField={facilityAssignmentsSortField}
      sortOrder={facilityAssignmentsSortOrder}
      disableSorting={isNewFacilityAdding || editableFacilityIndex !== null}
    />
  );

  const CohortAssignmentTableHeader = ({ handleSorting }: Pick<ITableHeader, 'handleSorting'>) => (
    <TableHeaderGenerator
      columns={cohortAssignmentColumns}
      handleSorting={handleSorting}
      sortField={cohortAssignmentsSortField}
      sortOrder={cohortAssignmentsSortOrder}
      disableSorting={isNewCohortAdding || editableCohortIndex !== null}
    />
  );

  const TreatmentPlanTableHeader = ({ columns, handleSorting }: ITableHeader) => (
    <TableHeaderGenerator
      columns={columns}
      handleSorting={handleSorting}
      sortField={treatmentPlansSortField}
      sortOrder={treatmentPlansSortOrder}
    />
  );

  const PolicyTableHeader = ({ columns, handleSorting }: ITableHeader) => (
    <TableHeaderGenerator
      columns={columns}
      handleSorting={handleSorting}
      sortField={policySortField}
      sortOrder={policySortOrder}
    />
  );

  const handleResetAssessmentAllocation = async () => {
    const data = {
      date_allocated: assementAllocationDate
        ? moment(assementAllocationDate).format('YYYY-MM-DD')
        : null,
    };
    const res = await dispatch(
      clientActions.resetAssessmentAllocation({ getAccessTokenSilently, clientId, data }),
    );

    if (!res.error) {
      dispatch(clientActions.getClient({ getAccessTokenSilently, clientId }));
    }
  };

  const handleEditClient = () => {
    history.navigate(`admin/client/${clientId}/edit`);
  };

  const handleUpdateClinicalStatus = async () => {
    if (
      !selectedClinicalStatus ||
      !client ||
      (selectedClinicalStatus === ClinicalStatus.OFFBOARDED && !selectedClinicalStatusReason)
    )
      return;

    const activeCohortAssignments = cohortAssignments.filter(
      (assignment) => !assignment.date_end || moment(assignment.date_end).isAfter(moment()),
    );

    if (
      selectedClinicalStatus === ClinicalStatus.CLINICAL &&
      activeCohortAssignments.length === 0
    ) {
      toast.error(
        'Client must be assigned to at least one active cohort to be promoted to clinical',
      );
      return;
    }

    const res = await dispatch(
      clientActions.updateClientClinicalStatus({
        getAccessTokenSilently,
        clientId: client?.id,
        data: {
          status: selectedClinicalStatus,
          reason: selectedClinicalStatusReason ? selectedClinicalStatusReason : null,
        },
      }),
    );
    if (!res.error && res.payload) {
      const { clinical_status, dob, first_name, last_name, sex, id } = res.payload;
      const updatedClientData = {
        clinical_status,
        dob,
        first_name,
        id,
        last_name,
        sex,
        client,
        middle_name: client.middle_name,
        email: client.email,
        street_address_line_one: client.street_address_line_one,
        street_address_line_two: client.street_address_line_two,
        street_address_zip: client.street_address_zip,
        street_address_city: client.street_address_city,
        street_address_state: client.street_address_state,
        phone: client.phone,
      };

      const updatedAllClientsList = allClientsList.map((client: IClient) => {
        if (client.id === res.payload.id) {
          return updatedClientData;
        }
        return client;
      });

      let newClinicalClientList = clinicalClientList;
      const clientInClinicalList = clinicalClientList.find(
        (client: IClient) => client.id === res.payload.id,
      );
      if (
        clinical_status === ClinicalStatus.CLINICAL &&
        !clientInClinicalList &&
        clinicalClientList.length // if client list not fetched yet, do not update, it will be populated as soon as the user move to admin dashboard
      ) {
        newClinicalClientList = [...clinicalClientList, updatedClientData].sort(
          (clientA: IClient, clientB: IClient) =>
            (clientA?.first_name + clientA?.last_name)?.toLowerCase() <
            (clientB?.first_name + clientB?.last_name)?.toLowerCase()
              ? -1
              : 1,
        );
      }
      if (clinical_status !== ClinicalStatus.CLINICAL && clientInClinicalList) {
        newClinicalClientList = clinicalClientList.filter(
          (client: IClient) => client.id !== res.payload.id,
        );
      }
      const updatedClinicalClientList = newClinicalClientList.map((client: IClient) => {
        if (client.id === res.payload.id) {
          return updatedClientData;
        }
        return client;
      });

      dispatch(
        clientActions.updateState({
          key: 'allClientsList',
          value: updatedAllClientsList,
        }),
      );

      dispatch(
        clientActions.updateState({
          key: 'list',
          value: updatedClinicalClientList,
        }),
      );
    }

    setSelectedClinicalStatus('');
    setSelectedClinicalStatusReason('');
  };

  const isLoading =
    clientLoading ||
    payorsLoading ||
    treatmentPlanLoading ||
    providerAssignmentLoading ||
    cohortAssignmentLoading;

  return (
    <PageContainer
      sx={{
        px: {
          xs: 2,
          sm: 3,
          md: 3,
          lg: 4,
        },
      }}
    >
      <Box sx={{ mt: 3 }} textAlign={'left'}>
        <FHPageLink to="/admin" label="Admin Home" />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box>
            {clientLoading && <FHSkeletonLoader width={200} height={24} />}
            {client && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h3">
                  {client?.first_name} {client?.last_name}
                </Typography>
                <FHChip
                  sx={{
                    fontSize: '13px',
                    fontWeight: '700',
                    lineHeight: '18px',
                  }}
                  content={client?.clinical_status}
                  bgColor={clinicalClient ? FORTA_SUCCESS : FORTA_ORANGE}
                  color={FORTA_WHITE}
                />

                {client?.clinical_status === ClinicalStatus.OFFBOARDED && (
                  <FHChip
                    sx={{
                      fontSize: '13px',
                      fontWeight: '700',
                      lineHeight: '18px',
                    }}
                    content={client?.clinical_status_reason}
                    bgColor={FORTA_ORANGE}
                    color={FORTA_WHITE}
                  />
                )}
              </Box>
            )}
          </Box>
          <Stack direction="row" spacing={2}>
            {!isLoading &&
              areUserRolesIn(user?.roles, [Roles.Admin, Roles.OnboardingCoordinator]) && (
                <FHButton
                  startIcon={<EditOutlinedIcon color={FORTA_WHITE} width={18} />}
                  onClick={handleEditClient}
                >
                  Edit
                </FHButton>
              )}
          </Stack>
        </Box>
        <hr></hr>
        <Box mb={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} lg={showClinicalStatusSection ? 3 : 6} mt={4}>
              <FBoxContainer sx={{ height: '100%', borderRadius: '8px', boxShadow: 'none' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  <Typography variant="h6" mb={2}>
                    Basic Info
                  </Typography>
                  <FHKeyValue
                    label="Name"
                    value={`${client?.first_name} ${client?.last_name}`}
                    loading={clientLoading}
                  />
                  <FHKeyValue
                    label="Client Id"
                    value={String(client?.id)}
                    loading={clientLoading}
                  />
                  <FHKeyValue
                    label="DOB"
                    value={client?.dob ? moment(client?.dob).format('DD-MMM-YYYY') : ''}
                    loading={clientLoading}
                  />
                  <FHKeyValue label="Gender" value={client?.sex} loading={clientLoading} />
                </Box>
              </FBoxContainer>
            </Grid>
            <Grid item xs={12} lg={showClinicalStatusSection ? 3 : 6} mt={4}>
              <FBoxContainer sx={{ height: '100%', borderRadius: '8px', boxShadow: 'none' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  <Typography variant="h6" mb={2}>
                    Other Info
                  </Typography>
                  <FHKeyValue
                    label="Address"
                    value={
                      client
                        ? `${client.street_address_line_one}, ${client.street_address_line_two}, ${client.street_address_city}, ${client.street_address_state}, ${client.street_address_zip}`
                        : ''
                    }
                    loading={clientLoading}
                  />
                  <FHKeyValue
                    label="Billing Facility"
                    values={billingFacilities.map((cf: IClientFacilityMap) => cf.facility.label)}
                    emptyStateText={'No active billing facility'}
                    loading={facilityLoading || !isFacilityDataFiltered}
                  />
                  <FHKeyValue
                    label="Service Location"
                    values={serviceFacilities.map((cf: IClientFacilityMap) => cf.facility.label)}
                    emptyStateText={'No active service facility'}
                    loading={facilityLoading || !isFacilityDataFiltered}
                  />
                </Box>
              </FBoxContainer>
            </Grid>
            {showClinicalStatusSection && (
              <Grid item xs={12} lg={3} mt={4}>
                <FBoxContainer
                  dataTestId="updateClientOnboardingStatus"
                  sx={{ height: '100%', flex: 1, borderRadius: '8px', boxShadow: 'none' }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-around',
                    }}
                  >
                    <Box>
                      <Typography variant="h6">Update onboarding status</Typography>
                      <Typography mt={2} mr={1} fontWeight={700} display="inline-block">
                        Current:
                      </Typography>
                      <Typography display="inline-block">
                        {client?.clinical_status ?? ''}
                      </Typography>

                      <Box sx={{ mt: 3 }}>
                        <Typography mb={1} fontWeight={700}>
                          Change to:
                        </Typography>
                        <FHSelect
                          sx={{
                            flexGrow: 1,
                            width: '100%',
                            boxShadow: 'none',
                            borderRadius: '8px',
                            mr: 1,
                          }}
                          inputHeight={40}
                          options={Object.values(ClinicalStatus)
                            .filter((val) => val !== client?.clinical_status)
                            .map((val) => ({ value: val, label: val }))}
                          value={selectedClinicalStatus}
                          onUpdate={(value: SelectValueType) => {
                            if (!isClinicalStatus(value)) return;
                            setSelectedClinicalStatus(value);
                            setSelectedClinicalStatusReason('');
                          }}
                        />
                      </Box>

                      {selectedClinicalStatus === ClinicalStatus.OFFBOARDED && (
                        <Box sx={{ mt: 3 }}>
                          <Typography mb={1} fontWeight={700}>
                            Reason:
                          </Typography>
                          <FHSelect
                            sx={{
                              flexGrow: 1,
                              width: '100%',
                              boxShadow: 'none',
                              borderRadius: '8px',
                              mr: 1,
                            }}
                            inputHeight={40}
                            disabled={!selectedClinicalStatus}
                            options={Object.values(ClientClinicalStatusReason).map((val) => ({
                              value: val,
                              label: val,
                            }))}
                            value={selectedClinicalStatusReason}
                            onUpdate={(value: SelectValueType) => {
                              if (!isClinicalStatusReason(value)) return;
                              setSelectedClinicalStatusReason(value);
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <FHButton
                        onClick={handleUpdateClinicalStatus}
                        sx={{ mt: 3, width: '100px' }}
                        disabled={
                          !selectedClinicalStatus ||
                          (selectedClinicalStatus === ClinicalStatus.OFFBOARDED &&
                            !selectedClinicalStatusReason)
                        }
                      >
                        Update
                      </FHButton>
                    </Box>
                  </Box>
                </FBoxContainer>
              </Grid>
            )}
            {areUserRolesIn(user?.roles, [Roles.Admin]) && clinicalClient && (
              <Grid item xs={12} lg={3} mt={4}>
                <FBoxContainer
                  dataTestId="resetAssessmentAllocation"
                  sx={{ height: '100%', flex: 1, borderRadius: '8px', boxShadow: 'none' }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-around',
                    }}
                  >
                    <Box>
                      <Typography variant="h6">Reset assessment allocation</Typography>
                      <Box sx={{ mt: 3 }}>
                        <Typography mb={1} fontWeight={700}>
                          Select date:
                        </Typography>
                        <FHAntdDateTimePicker
                          format="MM/DD/YYYY"
                          inputHeight={40}
                          sx={{ width: '100%', boxShadow: 'none' }}
                          inputReadOnly={true}
                          allowClear={false}
                          value={assementAllocationDate ? moment(assementAllocationDate) : null}
                          onSelect={(value: Moment | null) => {
                            setAssessmentAllocationDate(value ? value.format('YYYY-MM-DD') : null);
                          }}
                          onChange={(value: Moment | null) => {
                            setAssessmentAllocationDate(value ? value.format('YYYY-MM-DD') : null);
                          }}
                        />
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <FHButton
                        sx={{ mt: 3, width: '100px' }}
                        disabled={!assementAllocationDate || isLoading}
                        onClick={handleResetAssessmentAllocation}
                      >
                        Reset
                      </FHButton>
                    </Box>
                  </Box>
                </FBoxContainer>
              </Grid>
            )}
          </Grid>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} lg={12} mt={4}>
            <FBoxContainer
              dataTestId="clientTpDetails"
              sx={{ borderRadius: '20px', boxShadow: 'none' }}
            >
              <Grid container direction="column">
                <Box
                  sx={{
                    mb: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant="h4" textAlign="left">
                    Treatment Plans:
                  </Typography>
                  <FSearch
                    sx={{
                      borderRadius: '8px',
                      mr: 3.5,
                      backgroundColor: FORTA_WHITE,
                    }}
                    value={treatmentPlanSearchStr}
                    autoComplete="off"
                    inputHeight={40}
                    placeholder="Search by BCBA name or status"
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleTreatmentPlanSearch(e.target.value)
                    }
                  />
                </Box>
                {providerAssignments.length > 0 && !currentHomeBCBA && pendingHomeBCBA && (
                  <Box sx={{ display: 'flex', mb: 2, alignContent: 'center' }}>
                    <AttentionIcon sx={{ color: FORTA_ERROR }} />
                    <Typography textAlign="left" ml={1} fontWeight={'bold'} color={FORTA_ERROR}>
                      Attention:
                    </Typography>
                    <Typography textAlign="left" ml={1} color={FORTA_ERROR}>
                      Treatment plans cannot be reassigned right now as there is no active HOME BCBA
                    </Typography>
                  </Box>
                )}
                {sortedTreatmentPlans.length ? (
                  <>
                    <TreatmentPlanTableHeader
                      columns={treatmentPlanColumns}
                      handleSorting={sortTreatmentPlans}
                    />
                    {sortedTreatmentPlans.map((tp: ITreatmentPlan) => (
                      <Grid
                        key={tp.id}
                        container
                        sx={{
                          display: 'flex',
                          p: 1.5,
                          backgroundColor: FORTA_WHITE,
                          mb: 2,
                          borderRadius: '10px',
                          direction: 'row',
                          alignItems: 'center',
                        }}
                      >
                        <Grid item xs={1.2}>
                          <Link target="_blank" to={`/treatment-plan/${tp.id}/client-info`}>
                            {tp.id}
                          </Link>
                        </Grid>
                        <Grid item xs={3.4} textAlign={'center'}>
                          {`${tp?.provider?.first_name} ${tp?.provider?.last_name}`}
                        </Grid>
                        <Grid item xs={1.2} textAlign={'center'}>
                          {getTPEditingStatusfromId(tp.status_id)}
                        </Grid>
                        <Grid item xs={2} textAlign={'center'}>
                          {tp.is_active ? 'Yes' : 'No'}
                        </Grid>
                        <Grid item xs={2} textAlign={'center'}>
                          {moment(tp.date_treatment_plan_started).format('MM-DD-YYYY')}
                        </Grid>
                        <Grid item xs={2.2}>
                          {currentHomeBCBA &&
                            tp?.provider?.id !== currentHomeBCBA?.id &&
                            tp?.status_id !== 4 && (
                              <IconButton
                                sx={{
                                  border: `2px solid ${FORTA_BLUE.primary}`,
                                  borderRadius: '20px',
                                  mr: 3,
                                  gap: 1,
                                }}
                                onClick={() => handleReassignTP(tp?.id || 0)}
                              >
                                <ReAssignIcon sx={{ color: FORTA_BLUE.primary }} />
                                <Typography color={FORTA_BLUE.primary}>
                                  Assign TP to {currentHomeBCBA?.first_name}{' '}
                                  {currentHomeBCBA?.last_name}
                                </Typography>
                              </IconButton>
                            )}
                        </Grid>
                      </Grid>
                    ))}
                  </>
                ) : treatmentPlanLoading ? (
                  <Box sx={{ mt: 8 }}>
                    {Array.from({
                      length: sortedTreatmentPlans?.length ? sortedTreatmentPlans.length : 3,
                    }).map((_, index) => (
                      <AdminRowLoader key={index} />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="h6" textAlign="center" mb={2}>
                    No Treatment Plans found for this client
                  </Typography>
                )}
              </Grid>
            </FBoxContainer>
          </Grid>
          <Grid item xs={12} lg={12} mt={4}>
            <FBoxContainer
              sx={{
                borderRadius: '20px',
                boxShadow: 'none',
              }}
            >
              <Grid item xs={12} md={12} lg={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
                  <Box
                    ml={1}
                    mb={2}
                    sx={{
                      display: 'flex',
                      width: '100%',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography textAlign={'left'} variant="h4">
                      Provider assignments:
                    </Typography>
                    <FHTooltip
                      arrow
                      title={
                        editableAssignmentId !== null || isNewAssignmentAdding ? (
                          <Typography>
                            Please save or discard changes to enable this filter
                          </Typography>
                        ) : null
                      }
                      placement="left-start"
                      sx={{
                        borderRadius: '10px',
                        display: 'flex',
                        maxWidth: '350px',
                      }}
                      tooltipbackground={FORTA_WHITE}
                      tooltipBoxShadow={'0px 4px 4px rgba(0, 0, 0, 0.25)'}
                    >
                      <Box>
                        <FCheckbox
                          checked={showAllAssignments}
                          sx={{
                            opacity:
                              editableAssignmentId !== null || isNewAssignmentAdding ? 0.5 : 1,
                          }}
                          disabled={editableAssignmentId !== null || isNewAssignmentAdding}
                          onChange={(e) => setShowAllAssignments(e.target.checked)}
                          label={'Show all assignments'}
                        />
                      </Box>
                    </FHTooltip>
                  </Box>

                  {areUserRolesIn(user?.roles, [Roles.Admin, Roles.OnboardingCoordinator]) && (
                    <Box
                      sx={{
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <FHButton
                        sx={{
                          width: 250,
                        }}
                        startIcon={<AddIcon />}
                        disabled={isNewAssignmentAdding}
                        onClick={handleAddProviderAssignment}
                      >
                        Add new assignment
                      </FHButton>
                      <FSearch
                        sx={{
                          borderRadius: '8px',
                          mr: 3.5,
                          backgroundColor: FORTA_WHITE,
                        }}
                        value={providerSearchStr}
                        autoComplete="off"
                        inputHeight={40}
                        placeholder="Provider name or clinical relationship"
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleProviderSearch(e.target.value)
                        }
                      />
                    </Box>
                  )}

                  {assignments?.length > 0 && !providerLoading ? (
                    <>
                      <ProviderAssignmentTableHeader
                        columns={providerAssignmentColumns}
                        handleSorting={sortProviderAssignments}
                      />

                      {filteredProviderAssignment.map((assignment) => (
                        <ProviderAssignmentRow
                          key={assignment.id}
                          providerAssignment={assignment}
                          isAdding={isNewAssignmentAdding && assignment.id === undefined}
                          isEditing={
                            editableAssignmentId === assignment.id ||
                            (editableAssignmentId === NEW_PROVIDER_ASSIGNMENT_ID && !assignment.id)
                          }
                          isDisabled={providerAssignmentLoading}
                          onCancel={handleCancelAssignment}
                          onEdit={() => {
                            setEditableAssignmentId(assignment.id ?? null);
                          }}
                          onSave={handleAssignProvider}
                          onDelete={handleDeleteAssignment}
                        />
                      ))}
                    </>
                  ) : providerLoading || providerAssignmentLoading ? (
                    <Box sx={{ mt: 8 }}>
                      {Array.from({
                        length: filteredProviderAssignment?.length
                          ? filteredProviderAssignment.length
                          : 3,
                      }).map((_, index) => (
                        <AdminRowLoader key={index} />
                      ))}
                    </Box>
                  ) : (
                    <Typography
                      sx={{
                        p: 1,
                        textAlign: 'center',
                      }}
                    >
                      No provider assignments found for this client
                    </Typography>
                  )}
                </Box>
              </Grid>
            </FBoxContainer>
          </Grid>

          <Grid item xs={12} lg={12} mt={4}>
            <FBoxContainer
              sx={{
                borderRadius: '20px',
                boxShadow: 'none',
              }}
            >
              <Grid item xs={12} md={12} lg={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
                  <Box ml={1} mb={2} sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                    <Typography textAlign={'left'} variant="h4">
                      Facility assignments:
                    </Typography>
                  </Box>

                  {areUserRolesIn(user?.roles, [Roles.Admin, Roles.OnboardingCoordinator]) && (
                    <Box
                      sx={{
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <FHButton
                        sx={{
                          width: 240,
                        }}
                        startIcon={<AddIcon />}
                        disabled={isNewFacilityAdding}
                        onClick={handleAddFacilityAssignment}
                      >
                        Add new assignment
                      </FHButton>
                      <FSearch
                        sx={{
                          borderRadius: '8px',
                          mr: 3.5,
                          backgroundColor: FORTA_WHITE,
                        }}
                        value={facilitySearchStr}
                        autoComplete="off"
                        inputHeight={40}
                        placeholder="Facility name or assignment type"
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleFacilitySearch(e.target.value)
                        }
                      />
                    </Box>
                  )}

                  {clientFacilities?.length > 0 ? (
                    <>
                      <FacilityAssignmentTableHeader
                        columns={facilityAssignmentColumns}
                        handleSorting={sortFacilityAssignments}
                      />
                      {sortedFacilityAssignments.map((facility, index) => (
                        <FacilityAssignmentRow
                          key={index}
                          facilityAssignment={facility}
                          isAdding={isNewFacilityAdding && index === 0}
                          isEditing={editableFacilityIndex === index}
                          isDisabled={clientLoading}
                          onCancel={handleCancelFacilityAssignment}
                          onEdit={() => {
                            setEditableFacilityIndex(index);
                          }}
                          onSave={handleAssignFacility}
                          onDelete={handleDeleteFacilityAssignment}
                        />
                      ))}
                    </>
                  ) : facilityLoading || facilityAssignmentsLoading ? (
                    <Box sx={{ mt: 8 }}>
                      {Array.from({
                        length: sortedFacilityAssignments?.length
                          ? sortedFacilityAssignments.length
                          : 3,
                      }).map((_, index) => (
                        <AdminRowLoader key={index} />
                      ))}
                    </Box>
                  ) : (
                    <Typography
                      sx={{
                        p: 1,
                        textAlign: 'center',
                      }}
                    >
                      No facility assignments found for this client
                    </Typography>
                  )}
                </Box>
              </Grid>
            </FBoxContainer>
          </Grid>

          {/* Cohort Assignments */}
          {
            <>
              <Grid item xs={12} lg={12} mt={4}>
                <FBoxContainer
                  sx={{
                    borderRadius: '20px',
                    boxShadow: 'none',
                  }}
                >
                  <Grid item xs={12} md={12} lg={12}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
                      <Box
                        ml={1}
                        mb={2}
                        sx={{ display: 'flex', width: '100%', alignItems: 'center' }}
                      >
                        <Typography textAlign={'left'} variant="h4">
                          Cohort assignments:
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          mb: 3,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        {canAddCohortAssignment && (
                          <FHButton
                            sx={{
                              width: 240,
                            }}
                            startIcon={<AddIcon />}
                            disabled={isNewCohortAdding}
                            onClick={handleAddCohortAssignment}
                          >
                            Add new assignment
                          </FHButton>
                        )}
                        <FSearch
                          sx={{
                            borderRadius: '8px',
                            mr: 3.5,
                            backgroundColor: FORTA_WHITE,
                          }}
                          value={cohortSearchStr}
                          autoComplete="off"
                          inputHeight={40}
                          placeholder="Cohort  name"
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            handleCohortSearch(e.target.value)
                          }
                        />
                      </Box>

                      {cohortAssignments?.length > 0 ? (
                        <>
                          <CohortAssignmentTableHeader handleSorting={sortCohortAssignments} />
                          {sortedCohortAssignments.map((assignment, index) => (
                            <CohortAssignmentRow
                              key={index}
                              clientAssignment={assignment}
                              isAdding={isNewCohortAdding && index === 0}
                              isEditing={editableCohortIndex === index}
                              isDisabled={cohortAssignmentLoading}
                              showActions={canModifyCohortAssignment}
                              onCancel={handleCancelCohortAssignment}
                              onEdit={() => {
                                setEditableCohortIndex(index);
                              }}
                              onSave={handleAssignCohort}
                              onDelete={handleDeleteCohortAssignment}
                              mode={CohortAssignmentMode.Client}
                            />
                          ))}
                        </>
                      ) : cohortAssignmentLoading ? (
                        <Box sx={{ mt: 8 }}>
                          {Array.from({
                            length: 3,
                          }).map((_, index) => (
                            <AdminRowLoader key={index} />
                          ))}
                        </Box>
                      ) : (
                        <Typography
                          sx={{
                            p: 1,
                            textAlign: 'center',
                          }}
                        >
                          No cohort assignments found for this client
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </FBoxContainer>
              </Grid>
            </>
          }
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} lg={12} mt={4}>
            <FBoxContainer
              sx={{
                borderRadius: '20px',
                boxShadow: 'none',
              }}
            >
              <Grid container direction="column">
                <Box
                  sx={{
                    mb: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant="h4" textAlign="left">
                    Policies:
                  </Typography>
                  <Box>
                    <FSearch
                      sx={{
                        borderRadius: '8px',
                        mr: 3.5,
                        backgroundColor: FORTA_WHITE,
                      }}
                      value={policySearchStr}
                      autoComplete="off"
                      inputHeight={40}
                      placeholder="Policy payor or group number"
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        handlePolicySearch(e.target.value)
                      }
                    />
                    {areUserRolesIn(user?.roles, [Roles.Admin, Roles.OnboardingCoordinator]) && (
                      <FHButton
                        startIcon={<AddIcon />}
                        data-testid="createPolicyButton"
                        onClick={() => history.navigate(`admin/create-policy/${clientId}`)}
                      >
                        Create Policy
                      </FHButton>
                    )}
                  </Box>
                </Box>
                {policyErrors?.length > 0 && (
                  <Box sx={{ display: 'flex', mb: 2, alignContent: 'center' }}>
                    <AttentionIcon sx={{ color: FORTA_ERROR }} />
                    <Typography textAlign="left" ml={1} fontWeight={'bold'} color={FORTA_ERROR}>
                      Attention:
                    </Typography>
                    <Typography textAlign="left" ml={1} color={FORTA_ERROR}>
                      {policyErrors}
                    </Typography>
                  </Box>
                )}
                <PolicyTableHeader columns={policyColumns} handleSorting={sortPolicies} />
                {sortedPolicies.length ? (
                  sortedPolicies.map((policy: IClientPolicyMap, index: number) => (
                    <Box
                      key={policy.id ?? index}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        setSelectedPolicyForPreview(policy);
                        setShowPolicyPreview(true);
                      }}
                    >
                      <Grid
                        container
                        sx={{
                          display: 'flex',
                          p: 1.5,
                          backgroundColor: FORTA_WHITE,
                          mb: 2,
                          borderRadius: '10px',
                          direction: 'row',
                          alignItems: 'center',
                        }}
                      >
                        <Grid item xs={1.1} sx={{ textAlign: 'center' }}>
                          <Link to={`/admin/edit-policy/${clientId}/${policy?.id}`}>
                            {policy.id ?? 'N/A'}
                          </Link>
                        </Grid>
                        <Grid item xs={1.8} sx={{ textAlign: 'center' }}>
                          {policy.payor_responsibility ?? 'N/A'}
                        </Grid>
                        <Grid item xs={1.8} sx={{ textAlign: 'center' }}>
                          {policy.relationship_to_policyholder ?? 'N/A'}
                        </Grid>
                        <Grid
                          item
                          xs={1.5}
                          sx={{
                            textAlign: 'center',
                            wordBreak: 'break-word',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {policy.group_number ?? 'N/A'}
                        </Grid>
                        <Grid item xs={1.8} sx={{ textAlign: 'center' }}>
                          {moment(policy.date_start).format('MM/DD/YY') ?? 'N/A'}
                        </Grid>
                        <Grid item xs={1.9} sx={{ textAlign: 'center' }}>
                          {policy.date_end ? moment(policy.date_end).format('MM/DD/YY') : 'N/A'}
                        </Grid>
                        <Grid item xs={1.3} sx={{ textAlign: 'center' }}>
                          <Link to={`/admin-add-payor/${policy.payor?.id}`}>
                            {policy.payor?.label}
                          </Link>
                        </Grid>
                        <Grid item xs={0.8} sx={{ display: 'flex', justifyContent: 'center' }}>
                          <IconButton
                            sx={{
                              width: 48,
                              height: 30,
                              border: `2px solid ${FORTA_BLUE.primary}`,
                              borderRadius: '20px',
                              backgroundColor: 'transparent',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                            onClick={() =>
                              history.navigate(`/admin/edit-policy/${clientId}/${policy?.id}`)
                            }
                          >
                            <EditOutlinedIcon color={FORTA_BLUE.primary} width={18} />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Box>
                  ))
                ) : selectedClientPolicyLoading ? (
                  <Box sx={{ mt: 8 }}>
                    {Array.from({
                      length: 3,
                    }).map((_, index) => (
                      <AdminRowLoader key={index} />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="h6" textAlign="center" mb={2}>
                    No Policy found for this client
                  </Typography>
                )}
                <Grid item xs={12} lg={6} md={8} mt={4}>
                  <Grid container spacing={4} direction="row">
                    <Grid item xs={12} md={6} lg={6}></Grid>
                  </Grid>
                </Grid>
              </Grid>
            </FBoxContainer>
          </Grid>
        </Grid>
        {canManageModifiers && (
          <ClientModifierMatrixSection clientId={clientId} payorOptions={modifierPayorOptions} />
        )}
      </Box>
      {showPolicyPreview && (
        <FHSideDrawer
          sx={{ px: 2, py: 1 }}
          open={true}
          onClose={() => {
            setShowPolicyPreview(false);
          }}
        >
          <Grid container fontSize={16} spacing={3} textAlign="left" minWidth={1024}>
            <Grid item xs={12} sm={12} md={3}>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', my: 2 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Policy Info
                </Typography>
                <FHKeyValue label="Client" value={`${client?.first_name} ${client?.last_name}`} />
                <FHKeyValue label="Policy Id" value={`${selectedPolicyForPreview?.id}`} />
                <FHKeyValue label="Payor" value={`${selectedPolicyForPreview?.payor?.label}`} />
                <FHKeyValue
                  label="Start Date"
                  value={`${moment(selectedPolicyForPreview?.date_start).format('MM/DD/YY')}`}
                />
                <FHKeyValue
                  label="End Date"
                  value={
                    selectedPolicyForPreview?.date_end
                      ? `${moment(selectedPolicyForPreview?.date_end).format('MM/DD/YY')}`
                      : ''
                  }
                />
                <FHKeyValue
                  label="Member id"
                  value={`${selectedPolicyForPreview?.member_id ?? 'N/A'}`}
                />
                <FHKeyValue
                  label="Relatioship to policy holder"
                  value={`${capitalizeFirstLetter(
                    selectedPolicyForPreview?.relationship_to_policyholder?.toLowerCase() ?? '',
                  )}`}
                />
                <FHKeyValue
                  label="Payor responsibility"
                  value={`${capitalizeFirstLetter(
                    selectedPolicyForPreview?.payor_responsibility?.toLowerCase() ?? '',
                  )}`}
                />
                <FHKeyValue
                  label="Group number"
                  value={`${selectedPolicyForPreview?.group_number ?? ''}`}
                />
                <FHKeyValue
                  label="Is PA1 required"
                  value={`${selectedPolicyForPreview?.is_PA1_required ? 'Yes' : 'No'}`}
                />
                <FHKeyValue
                  label="Is PA2 required"
                  value={`${selectedPolicyForPreview?.is_PA2_required ? 'Yes' : 'No'}`}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={12} md={3}>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', my: 2 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Payor Info
                </Typography>
                <FHKeyValue label="Name" value={`${selectedPolicyForPreview?.payor?.label}`} />
                <FHKeyValue label="State" value={`${selectedPolicyForPreview?.payor?.state}`} />
                <FHKeyValue
                  label="Modifier"
                  value={`${selectedPolicyForPreview?.payor?.modifier ?? 'N/A'}`}
                />
                <FHKeyValue
                  label="Is using taxonomy code"
                  value={`${
                    selectedPolicyForPreview?.payor?.is_using_taxonomy_code ? 'Yes' : 'No'
                  }`}
                />
                <FHKeyValue
                  label="Clearing house payor id"
                  value={`${selectedPolicyForPreview?.payor?.clearinghouse_payor_id ?? 'N/A'}`}
                />
                <FHKeyValue
                  label="Changehealthcare eligibility payor id"
                  value={`${
                    selectedPolicyForPreview?.payor?.changehealthcare_eligibility_payor_id ?? 'N/A'
                  }`}
                />
                <FHKeyValue label="Type" value={`${selectedPolicyForPreview?.payor?.type}`} />
                <FHKeyValue
                  label="Sector"
                  value={`${selectedPolicyForPreview?.payor?.sector ?? 'N/A'}`}
                />
                <FHKeyValue
                  label="Preferred service code type"
                  value={`${selectedPolicyForPreview?.payor?.preferred_service_code_type ?? 'N/A'}`}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={12} md={3}>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', my: 2 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Policyholder Info
                </Typography>
                <FHKeyValue
                  label="Name"
                  value={`${selectedPolicyForPreview?.policyholder?.name}`}
                />
                <FHKeyValue
                  label="Gender"
                  value={`${capitalizeFirstLetter(
                    selectedPolicyForPreview?.policyholder?.sex.toLowerCase() ?? '',
                  )}`}
                />
                <FHKeyValue
                  label="Dob"
                  value={
                    selectedPolicyForPreview?.policyholder?.dob
                      ? `${moment(selectedPolicyForPreview?.policyholder?.dob).format('MM/DD/YY')}`
                      : ''
                  }
                />
                <FHKeyValue
                  label="Phone"
                  value={`${selectedPolicyForPreview?.policyholder?.phone}`}
                />
                <FHKeyValue
                  label="Address"
                  value={`${selectedPolicyForPreview?.policyholder?.street_address_line_one}, ${
                    selectedPolicyForPreview?.policyholder?.street_address_line_two
                  }, ${
                    selectedPolicyForPreview?.policyholder?.street_address_city
                  }, ${selectedPolicyForPreview?.policyholder?.street_address_state?.toUpperCase()}, ${
                    selectedPolicyForPreview?.policyholder?.street_address_zip
                  }`}
                />
              </Box>
            </Grid>
          </Grid>
        </FHSideDrawer>
      )}
      <DeleteCohortAssignmentModal
        open={cohortDeleteModalOpen}
        assignment={cohortAssignmentPendingDelete}
        clientId={clientId}
        onClose={closeDeleteCohortModal}
        onDeleted={() => setIsNewCohortAdding(false)}
      />
    </PageContainer>
  );
}

export default ClientDetails;
